import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { Beat } from '../types/content';
import { contentService } from '../services/content';
import { storageService } from '../services/storage';
import { useAudioPlayer } from '../context/AudioPlayerContext';

export function useBeatDetails() {
  const { beatId } = useParams<{ beatId: string }>();
  const [beat, setBeat] = useState<Beat | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showRecorder, setShowRecorder] = useState(false);
  const [versesKey, setVersesKey] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [verseCount, setVerseCount] = useState(0);
  const [hasPublishedMix, setHasPublishedMix] = useState(false);
  const [isVoting, setIsVoting] = useState(false);
  const [userVote, setUserVote] = useState<1 | undefined>(undefined);
  const [voteCount, setVoteCount] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isUpdatingVersesCount, setIsUpdatingVersesCount] = useState(false);
  const [audioDuration, setAudioDuration] = useState(0);

  const { play, pause, isPlaying, stop, currentAudioKey, currentTime, duration, formatTime } =
    useAudioPlayer();

  const isBeatPlaying = isPlaying && currentAudioKey === beatId;

  const fetchBeatDetails = useCallback(async () => {
    if (!beatId) return;
    try {
      const { data, error: fetchError } = await supabase
        .from('beats')
        .select(`
          *,
          producer:profiles!beats_producer_id_fkey (
            id,
            username,
            state,
            role,
            created_at,
            updated_at
          )
        `)
        .eq('id', beatId)
        .single();

      if (fetchError) throw fetchError;

      const transformedBeat: Beat = {
        ...data,
        producer_id: data.producer.id,
        updated_at: data.updated_at || data.created_at,
      };

      setBeat(transformedBeat);
    } catch (err) {
      setError('Error fetching beat details');
      console.error('Error fetching beat details:', err);
    } finally {
      setIsLoading(false);
    }
  }, [beatId]);

  const fetchVerseCount = useCallback(async () => {
    if (!beatId) return;

    const approvedQuery = supabase
      .from('verses')
      .select('id', { count: 'exact', head: true })
      .eq('beat_id', beatId)
      .eq('status', 'approved');

    const { data, error: rpcError } = await supabase.rpc('get_beat_active_verse_count', {
      p_beat_id: beatId,
    });

    const { count: approvedCount, error: approvedError } = await approvedQuery;
    if (!approvedError && typeof approvedCount === 'number') {
      setHasPublishedMix(approvedCount > 0);
    }

    if (!rpcError && data != null) {
      setVerseCount(Number(data));
      return;
    }
    const { count, error: countError } = await supabase
      .from('verses')
      .select('id', { count: 'exact', head: true })
      .eq('beat_id', beatId)
      .neq('status', 'declined');
    if (!countError && typeof count === 'number') {
      setVerseCount(count);
    }
  }, [beatId]);

  const fetchBeatVotes = useCallback(async () => {
    if (!beatId) return;
    const { data: { user } } = await supabase.auth.getUser();
    const { data: votes, error: votesError } = await supabase
      .from('votes')
      .select('*')
      .eq('content_type', 'beat')
      .eq('content_id', beatId);
    if (!votesError && votes) {
      setVoteCount(votes.reduce((acc, v) => acc + (v.vote || 0), 0));
      setUserVote(user ? votes.find(v => v.user_id === user.id)?.vote : undefined);
    }
  }, [beatId]);

  useEffect(() => {
    setIsLoading(true);
    fetchBeatDetails();
    fetchVerseCount();
    fetchBeatVotes();
  }, [beatId, versesKey, fetchBeatDetails, fetchVerseCount, fetchBeatVotes]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      if (audioRef.current) {
        setAudioDuration(audioRef.current.duration);
      }
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    return () => audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
  }, [beat?.file_url]);

  const handleVote = async (value: 1 | -1) => {
    if (!beat) return;
    setIsVoting(true);
    try {
      if (value === 1) {
        await contentService.vote('beat', beat.id, 1);
      } else if (value === -1 && userVote === 1) {
        await contentService.deleteVote('beat', beat.id);
      }
      await fetchBeatVotes();
    } catch (err) {
      console.error('Error voting:', err);
    } finally {
      setIsVoting(false);
    }
  };

  const handleVersesCountChange = async (newCount: number) => {
    if (!beat || !beatId) return;

    setIsUpdatingVersesCount(true);
    try {
      await contentService.updateBeatVersesCount(beatId, newCount);
      setBeat(prev => (prev ? { ...prev, verses_count: newCount } : null));
    } catch (err) {
      console.error('Error updating verses count:', err);
      setError('Error updating verses count. Please try again.');
    } finally {
      setIsUpdatingVersesCount(false);
    }
  };

  const handleDeleteBeat = async () => {
    if (!beat) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      const { data: verses, error: versesFetchError } = await supabase
        .from('verses')
        .select('id, file_url')
        .eq('beat_id', beat.id);
      if (versesFetchError) throw versesFetchError;
      const verseIds = (verses || []).map((v: { id: string }) => v.id);

      if (verseIds.length > 0) {
        const { error: verseVotesError } = await supabase
          .from('votes')
          .delete()
          .in('content_id', verseIds)
          .eq('content_type', 'verse');
        if (verseVotesError) throw verseVotesError;
      }

      const { error: beatVotesError } = await supabase
        .from('votes')
        .delete()
        .eq('content_id', beat.id)
        .eq('content_type', 'beat');
      if (beatVotesError) throw beatVotesError;

      const { error: versesDeleteError } = await supabase
        .from('verses')
        .delete()
        .eq('beat_id', beat.id);
      if (versesDeleteError) throw versesDeleteError;

      const { error: beatError } = await supabase.from('beats').delete().eq('id', beat.id);
      if (beatError) throw beatError;

      // Best-effort storage cleanup (records are already gone; a failure here
      // only leaves orphaned files, which the cleanup script can reclaim)
      const versePaths = (verses || [])
        .map((v: { id: string; file_url?: string }) =>
          v.file_url ? storageService.getStoragePathFromUrl(v.file_url, 'verses') : null
        )
        .filter((p): p is string => !!p);
      if (versePaths.length > 0) {
        const { error: verseFilesError } = await supabase.storage
          .from('verses')
          .remove(versePaths);
        if (verseFilesError) {
          console.warn('Verse files removal failed:', verseFilesError);
        }
      }
      const beatPath = storageService.getStoragePathFromUrl(beat.file_url, 'beats');
      if (beatPath) {
        const { error: beatFileError } = await supabase.storage
          .from('beats')
          .remove([beatPath]);
        if (beatFileError) {
          console.warn('Beat file removal failed:', beatFileError);
        }
      }

      window.location.href = '/';
    } catch (err) {
      setDeleteError('Error deleting beat. Please try again.');
      setIsDeleting(false);
    }
  };

  const togglePlay = async () => {
    if (!beat || !audioRef.current) return;
    try {
      if (isBeatPlaying) {
        pause();
        return;
      }
      audioRef.current.src = beat.file_url;
      await play(audioRef.current, beat.id);
    } catch (err) {
      setError('Error playing beat');
      console.error('Error playing beat:', err);
    }
  };

  const handleVerseUploaded = () => {
    setShowRecorder(false);
    setVersesKey(prev => prev + 1);
    fetchVerseCount();
  };

  const progressPercent =
    isBeatPlaying && duration > 0
      ? (currentTime / duration) * 100
      : audioDuration > 0
        ? 0
        : 0;

  const displayCurrent = isBeatPlaying ? formatTime(currentTime) : '0:00';
  const displayDuration = isBeatPlaying
    ? formatTime(duration)
    : audioDuration > 0
      ? formatTime(audioDuration)
      : '0:00';

  return {
    beatId,
    beat,
    isLoading,
    error,
    showRecorder,
    setShowRecorder,
    versesKey,
    audioRef,
    verseCount,
    hasPublishedMix,
    isVoting,
    userVote,
    voteCount,
    showDeleteModal,
    setShowDeleteModal,
    isDeleting,
    deleteError,
    isUpdatingVersesCount,
    audioDuration,
    isBeatPlaying,
    togglePlay,
    handleVote,
    handleVersesCountChange,
    handleDeleteBeat,
    handleVerseUploaded,
    stop,
    progressPercent,
    displayCurrent,
    displayDuration,
  };
}
